
import { Component, Vue, Watch } from 'vue-property-decorator';
import { RepositoryFactory } from '@/api/RepositoryFactory';
import RatingRepository from '@/api/repositories/RatingRepository';
import CustomerRepository from '@/api/repositories/CustomerRepository';
import DriverRepository from '@/api/repositories/DriverRepository';

const ratingRepository: RatingRepository = RepositoryFactory.get('rating');
const customerRepository: CustomerRepository = RepositoryFactory.get('customer');
const driverRepository: DriverRepository = RepositoryFactory.get('driver');

/** What the pending reason dialog will do once a reason is confirmed. */
interface PendingAction {
    kind: 'rating' | 'customer' | 'driver',
    action: string,
    id: string
}

@Component({
    components: {
        ReasonDialogComponent: () => import(
            /* webpackChunkName: "ReasonDialogComponent" */
            '@/components/ReasonDialog.component.vue')
    }
})
export default class ModerationView extends Vue {
    public activeTab: number = 0;

    public isLoadingRatings: boolean = false;
    public isLoadingBlocked: boolean = false;
    public isActing: boolean = false;

    public ratings: any[] = [];
    public total: number = 0;
    public options: any = { page: 1, itemsPerPage: 25, sortBy: [], sortDesc: [] };

    public blockedCustomers: any[] = [];
    public blockedDrivers: any[] = [];

    public filters: { rate: number | null, moderationStatus: string | null, withCommentOnly: boolean } = {
        rate: null,
        moderationStatus: null,
        withCommentOnly: false
    };

    public statusOptions = ['VISIBLE', 'HIDDEN', 'DELETED'];
    public rateOptions = [
        { text: '1 – Badly', value: 1 },
        { text: '2 – Fine', value: 2 },
        { text: '3 – Good', value: 3 }
    ];

    public reasonDialog: {
        show: boolean, title: string, text: string,
        confirmLabel: string, confirmColor: string
    } = { show: false, title: '', text: '', confirmLabel: '', confirmColor: 'error' };

    private pendingAction: PendingAction | null = null;

    public get isLoading(): boolean {
        return this.isLoadingRatings || this.isLoadingBlocked;
    }

    public get ratingHeaders() {
        return [
            { text: this.$t('MODERATION.TABLE.DATE'), value: 'createdAt', sortable: false },
            { text: this.$t('MODERATION.TABLE.RATE'), value: 'rate', sortable: false },
            { text: this.$t('MODERATION.TABLE.COMMENT'), value: 'comment', sortable: false },
            { text: this.$t('MODERATION.TABLE.DRIVER'), value: 'driver', sortable: false },
            { text: this.$t('MODERATION.TABLE.CUSTOMER'), value: 'customer', sortable: false },
            { text: this.$t('MODERATION.TABLE.STATUS'), value: 'moderationStatus', sortable: false },
            { text: this.$t('MODERATION.TABLE.ACTIONS'), value: 'actions', sortable: false, align: 'right' }
        ];
    }

    public get blockedCustomerHeaders() {
        return [
            { text: this.$t('MODERATION.TABLE.NAME'), value: 'name', sortable: false },
            { text: this.$t('MODERATION.TABLE.PHONE'), value: 'phone', sortable: false },
            { text: this.$t('MODERATION.TABLE.REASON'), value: 'blockReason', sortable: false },
            { text: this.$t('MODERATION.TABLE.BLOCKED_AT'), value: 'blockedAt', sortable: false },
            { text: this.$t('MODERATION.TABLE.ACTIONS'), value: 'actions', sortable: false, align: 'right' }
        ];
    }

    public get blockedDriverHeaders() {
        return [
            { text: this.$t('MODERATION.TABLE.NAME'), value: 'name', sortable: false },
            { text: this.$t('MODERATION.TABLE.COMPANY'), value: 'company', sortable: false },
            { text: this.$t('MODERATION.TABLE.REASON'), value: 'blockReason', sortable: false },
            { text: this.$t('MODERATION.TABLE.BLOCKED_AT'), value: 'blockedAt', sortable: false },
            { text: this.$t('MODERATION.TABLE.ACTIONS'), value: 'actions', sortable: false, align: 'right' }
        ];
    }

    async created() {
        await this.refreshAll();
    }

    @Watch('options')
    private onOptionsChange() {
        this.loadRatings();
    }

    public async refreshAll() {
        await Promise.all([this.loadRatings(), this.loadBlocked()]);
    }

    /**
     * Customers are phone-only identities -- the customer app never collects a
     * name (0 of 9529 live documents have one), so the phone IS the label.
     */
    public customerLabel(customer: any): string {
        const name = `${customer.firstName || ''} ${customer.lastName || ''}`.trim();
        if (name) return name;
        if (customer.phone && customer.phone.prefix) {
            return `${customer.phone.prefix}${customer.phone.phoneNumber}`;
        }
        return '-';
    }

    public isVisible(item: any): boolean {
        // Ratings written before the moderation feature carry no
        // moderationStatus at all; they are visible.
        return !item.moderationStatus || item.moderationStatus === 'VISIBLE';
    }

    public rateColor(rate: number): string {
        switch (rate) {
            case 1: return 'error';
            case 2: return 'warning';
            default: return 'success';
        }
    }

    public statusColor(status: string): string {
        switch (status) {
            case 'HIDDEN': return 'warning';
            case 'DELETED': return 'error';
            default: return 'success';
        }
    }

    public onFilterChange() {
        // Reset to first page whenever a filter changes, then reload. Setting
        // options triggers the watcher, which reloads; changing filters on
        // page 1 has no options change, so reload directly.
        if (this.options.page !== 1) {
            this.options = { ...this.options, page: 1 };
        } else {
            this.loadRatings();
        }
    }

    private async loadRatings() {
        this.isLoadingRatings = true;
        try {
            const response = await ratingRepository.moderationList({
                page: this.options.page,
                pageSize: this.options.itemsPerPage,
                rate: this.filters.rate ?? undefined,
                moderationStatus: this.filters.moderationStatus ?? undefined,
                withCommentOnly: this.filters.withCommentOnly || undefined
            });
            this.ratings = response.data.rows ?? [];
            this.total = response.data.total ?? 0;
        } catch (e) {
            this.$notifyErrorSimplified('MODERATION.NOTIFICATIONS.LOAD_ERROR');
        } finally {
            this.isLoadingRatings = false;
        }
    }

    private async loadBlocked() {
        this.isLoadingBlocked = true;
        try {
            const [customers, drivers] = await Promise.all([
                customerRepository.listBlocked(),
                driverRepository.listBlocked()
            ]);
            this.blockedCustomers = customers.data ?? [];
            this.blockedDrivers = drivers.data ?? [];
        } catch (e) {
            this.$notifyErrorSimplified('MODERATION.NOTIFICATIONS.LOAD_ERROR');
        } finally {
            this.isLoadingBlocked = false;
        }
    }

    /* ---------------- reason-dialog plumbing ---------------- */

    public openRatingAction(item: any, action: 'hide' | 'delete' | 'restore') {
        this.pendingAction = { kind: 'rating', action, id: item._id || item.id };
        this.reasonDialog = {
            show: true,
            title: this.$t(`MODERATION.DIALOG.${action.toUpperCase()}_TITLE`).toString(),
            text: this.$t(`MODERATION.DIALOG.${action.toUpperCase()}_TEXT`).toString(),
            confirmLabel: this.$t(`MODERATION.ACTIONS.${action.toUpperCase()}`).toString(),
            confirmColor: action === 'restore' ? 'primary' : 'error'
        };
    }

    public openCustomerAction(customer: any, action: 'block' | 'unblock') {
        const name = this.customerLabel(customer);
        this.pendingAction = { kind: 'customer', action, id: customer._id || customer.id };
        this.reasonDialog = {
            show: true,
            title: this.$t(`MODERATION.DIALOG.${action.toUpperCase()}_TITLE`).toString(),
            text: this.$t(`MODERATION.DIALOG.${action.toUpperCase()}_TEXT`, { name }).toString(),
            confirmLabel: this.$t(`MODERATION.ACTIONS.${action.toUpperCase()}`).toString(),
            confirmColor: action === 'block' ? 'error' : 'primary'
        };
    }

    public openDriverUnblock(driver: any) {
        const name = `${driver.firstName || ''} ${driver.lastName || ''}`.trim();
        this.pendingAction = { kind: 'driver', action: 'unblock', id: driver._id || driver.id };
        this.reasonDialog = {
            show: true,
            title: this.$t('MODERATION.DIALOG.UNBLOCK_TITLE').toString(),
            text: this.$t('MODERATION.DIALOG.UNBLOCK_TEXT', { name }).toString(),
            confirmLabel: this.$t('MODERATION.ACTIONS.UNBLOCK').toString(),
            confirmColor: 'primary'
        };
    }

    public async onReasonConfirmed(reason: string) {
        if (!this.pendingAction) {
            return;
        }
        const { kind, action, id } = this.pendingAction;
        this.isActing = true;
        try {
            if (kind === 'rating') {
                if (action === 'hide') await ratingRepository.hide({ id, reason });
                else if (action === 'delete') await ratingRepository.moderationDelete({ id, reason });
                else await ratingRepository.restore({ id, reason });
            } else if (kind === 'customer') {
                if (action === 'block') await customerRepository.block({ id, reason });
                else await customerRepository.unblock({ id, reason });
            } else {
                await driverRepository.unblock({ id, reason });
            }
            this.$notifySuccessSimplified('MODERATION.NOTIFICATIONS.SUCCESS');
            this.closeReasonDialog();
            await this.refreshAll();
        } catch (e) {
            this.$notifyErrorSimplified('MODERATION.NOTIFICATIONS.ACTION_ERROR');
        } finally {
            this.isActing = false;
        }
    }

    public closeReasonDialog() {
        this.reasonDialog.show = false;
        this.pendingAction = null;
    }
}
